import { z } from 'zod';

/**
 * Realtime voice providers — speech-in/speech-out models that replace
 * the pipelined STT → LLM → TTS chain with a single multimodal model.
 *
 * Distinct from `LLM_PROVIDERS`, which is the text-LLM provider list
 * used on the pipelined path:
 *   - The pipelined LLM accepts text and produces text; the realtime
 *     provider accepts audio and produces audio. Different role.
 *   - Some text providers (groq, together, fireworks) have no realtime
 *     offering; some realtime providers (deepgram) aren't chat LLMs.
 *     One enum can't carry both without misleading semantics.
 *
 * Each value maps to a specific `@livekit/agents-plugin-*` integration:
 *   - 'openai'   → @livekit/agents-plugin-openai (RealtimeModel,
 *                  default model: gpt-realtime)
 *   - 'xai'      → @livekit/agents-plugin-xai (RealtimeModel extends
 *                  the openai one, default model: grok-voice-think-fast-1.0)
 *   - 'google'   → @livekit/agents-plugin-google (beta.realtime.RealtimeModel,
 *                  default model: gemini-2.5-flash-native-audio-preview).
 *                  Wired in v3.1 prototype; v2 throws (use V3_PROTOTYPE=true
 *                  to route to v3 when this provider is selected).
 *   - 'deepgram' → reserved for the next iteration; not yet wired in
 *                  buildV2RealtimeSession. Including in the enum now
 *                  so request validation accepts it once the plugin
 *                  branch lands.
 *
 * The runtime checks the value against its own switch in
 * `apps/livekit/src/v2/session/config.ts:buildV2RealtimeSession`. Any
 * value the runtime doesn't yet handle is treated as an error at session
 * boot (loud failure), not a silent fallback.
 */
export const REALTIME_PROVIDERS = z.enum(['openai', 'xai', 'google', 'deepgram']);

export type RealtimeProvider = z.infer<typeof REALTIME_PROVIDERS>;
